import { Dropbox, DropboxResponse, type files } from 'dropbox'
import type { UploadProgress } from '../utils/interface/upload_progress'
import type { DownloadResponse } from './dropbox_model'

class DropboxService {
  protected accessToken: string
  protected dat: string
  private dbx: Dropbox

  constructor() {
    this.accessToken = import.meta.env.VITE_DROPBOX_TOKEN
    this.dat = import.meta.env.VITE_ACCESS_TOKEN_DROPBOX
    this.dbx = new Dropbox({ accessToken: this.accessToken })
  }

  /**
   * Descarga un archivo de Dropbox
   * @param path Ruta completa del archivo en Dropbox
   * @returns Objeto con el blob y metadatos del archivo
   */
  async downloadFile(path: string): Promise<files.GetTemporaryLinkResult> {
    try {
      const resp = await this.dbx.filesGetTemporaryLink({ path: path })
      return resp.result
    } catch (error: any) {
      console.error('❌ Error descargando archivo:', error)
      throw error
    }
  }
  /**
   * Determina el content type basado en la extensión del archivo
   */
  private getContentType(filename: string): string {
    const ext = filename.toLowerCase().split('.').pop()

    const mimeTypes: Record<string, string> = {
      pdf: 'application/pdf',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      gif: 'image/gif',
      txt: 'text/plain',
      json: 'application/json',
      xml: 'application/xml',
      zip: 'application/zip',
      doc: 'application/msword',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      xls: 'application/vnd.ms-excel',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ppt: 'application/vnd.ms-powerpoint',
      pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      mp3: 'audio/mpeg',
      mp4: 'video/mp4',
      avi: 'video/x-msvideo',
      csv: 'text/csv',
      html: 'text/html',
      css: 'text/css',
      js: 'application/javascript',
    }

    return mimeTypes[ext || ''] || 'application/octet-stream'
  }
  /**
   * Descarga un archivo y lo guarda en el navegador
   * @param path Ruta del archivo en Dropbox
   * @param customFilename Nombre personalizado para el archivo descargado (opcional)
   */
  async downloadAndSaveFile(path: string, customFilename?: string): Promise<void> {
    try {
      const { blob, filename } = await this.downloadFile(path)

      // Crear URL temporal del blob
      const url = window.URL.createObjectURL(blob)

      // Crear elemento <a> para forzar la descarga
      const link = document.createElement('a')
      link.href = url
      link.download = customFilename || filename

      // Simular click para descargar
      document.body.appendChild(link)
      link.click()

      // Limpiar
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

    } catch (error) {
      console.error('❌ Error al guardar archivo:', error)
      throw error
    }
  }

  async getFiles(nameUser: string): Promise<DropboxResponse<files.ListFolderResult>> {
    try {
      const normalizedPath = `/PLATAFORMA HOLISTICA/${nameUser.trim()}`.replace(/\/+$/, '')
      const resp = await this.dbx.filesListFolder({
        path: normalizedPath,
        recursive: false, // Cambiar a true si necesitas subcarpetas
      })

      return resp
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'status' in error) {
        const dbxError = error as { status: number; error: unknown }

        if (dbxError.status === 409) {
          // Error 409: La carpeta no existe
          throw new Error(`La carpeta del usuario "${nameUser}" no existe en Dropbox`)
        }

        if (dbxError.status === 401) {
          // Error de autenticación
          throw new Error('Token de Dropbox inválido o expirado')
        }
      }

      const errorMessage = error instanceof Error ? error.message : String(error)
      throw new Error(`Error al obtener los archivos del usuario ${nameUser}: ${errorMessage}`)
    }
  }

  async searchFolder(
    query: string,
    options?: files.SearchOptions,
  ): Promise<DropboxResponse<files.SearchV2Result>> {
    try {
      const opt = options ?? {
        path: `/PLATAFORMA HOLISTICA`,
        max_results: 100,
        file_status: { '.tag': 'active' },
        filename_only: true,
        file_categories: [{ '.tag': 'folder' }],
      }
      const resp = await this.dbx.filesSearchV2({
        query: query,
        options: opt,
      })
      return resp
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'status' in error) {
        const dbxError = error as { status: number; error: unknown }

        if (dbxError.status === 409) {
          throw new Error(`La carpeta del usuario "${query}" no existe en Dropbox`)
        }
      }
      const errorMessage = error instanceof Error ? error.message : String(error)
      throw new Error(`Error al buscar query: ${query}: ${errorMessage}`)
    }
  }

  /**
   * The function verify if have folder in dropbox
   *  return true if have folder
   *  return false if don't have folder
   *  @params path is dir of the folder
   **/
  async verifyFolder(path: string, opt?: files.SearchOptions): Promise<boolean> {
    try {
      const option = opt ?? {
        path: `/PLATAFORMA HOLISTICA`,
        max_results: 100,
        file_status: { '.tag': 'active' },
        filename_only: true,
        file_categories: [{ '.tag': 'folder' }],
      }
      const resp = await this.dbx.filesSearchV2({
        query: path,
        options: option,
      })
      return resp.result.matches.length !== 0
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'status' in error) {
        const dbxError = error as { status: number; error: unknown }

        if (dbxError.status === 409) {
          throw new Error(`La carpeta del usuario "${path}" no existe en Dropbox`)
        }
      }
      const errorMessage = error instanceof Error ? error.message : String(error)
      throw new Error(`Error al buscar query: ${path}: ${errorMessage}`)
    }
  }

  async createFolder(path: string): Promise<boolean> {
    try {
      const resp = await this.dbx.filesCreateFolderV2({
        path: `/PLATAFORMA HOLISTICA/${path}`,
        autorename: false,
      })
      return resp.result.metadata.id !== null
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'status' in error) {
        const dbxError = error as { status: number; error: unknown }

        if (dbxError.status === 409) {
          throw new Error(`Error al crear la carpeta "${path}"`)
        }
      }
      const errorMessage = error instanceof Error ? error.message : String(error)
      throw new Error(`Error con la carpeta: ${path}: ${errorMessage}`)
    }
  }

  /**
   * Subir archivos pequeños (< 150MB)
   * Usa filesUpload - método directo y rápido
   */
  async uploadSmallFile(
    file: File,
    path: string,
    onProgress?: (progress: UploadProgress) => void,
  ): Promise<files.FileMetadata> {
    try {
      // Reportar inicio
      if (onProgress) {
        onProgress({ loaded: 0, total: file.size, percentage: 0 })
      }

      const contents = await file.arrayBuffer()

      const response = await this.dbx.filesUpload({
        path: path,
        contents: contents,
        mode: { '.tag': 'overwrite' },
        autorename: false,
        mute: false,
        strict_conflict: false,
      })

      // Reportar completado
      if (onProgress) {
        onProgress({ loaded: file.size, total: file.size, percentage: 100 })
      }

      return response.result
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      throw new Error(`Error al subir archivo: ${errorMessage}`)
    }
  }

  /**
   * Subir archivos grandes (> 150MB, hasta 350GB)
   * Usa filesUploadSessionStart + filesUploadSessionAppendV2 + filesUploadSessionFinish
   */
  async uploadLargeFile(
    file: File,
    path: string,
    onProgress?: (progress: UploadProgress) => void,
  ): Promise<files.FileMetadata> {
    const CHUNK_SIZE = 8 * 1024 * 1024 // 8MB por chunk (recomendado)
    let offset = 0
    let sessionId: string

    try {
      // 1. Iniciar sesión de upload con el primer chunk
      const firstChunk = file.slice(0, Math.min(CHUNK_SIZE, file.size))
      const firstChunkBuffer = await firstChunk.arrayBuffer()

      const startResponse = await this.dbx.filesUploadSessionStart({
        contents: firstChunkBuffer,
        close: false,
      })

      sessionId = startResponse.result.session_id
      offset = firstChunk.size

      // Reportar progreso del primer chunk
      if (onProgress) {
        const percentage = Math.round((offset / file.size) * 100)
        onProgress({ loaded: offset, total: file.size, percentage })
      }

      // 2. Subir chunks intermedios
      while (offset < file.size) {
        const chunk = file.slice(offset, Math.min(offset + CHUNK_SIZE, file.size))
        const chunkBuffer = await chunk.arrayBuffer()
        const isLastChunk = offset + chunk.size >= file.size

        if (isLastChunk) {
          // 3. Último chunk - finalizar sesión
          const finishResponse = await this.dbx.filesUploadSessionFinish({
            cursor: {
              session_id: sessionId,
              offset: offset,
            },
            commit: {
              path: path,
              mode: { '.tag': 'overwrite' },
              autorename: false,
              mute: false,
              strict_conflict: false,
            },
            contents: chunkBuffer,
          })

          offset += chunk.size

          // Reportar progreso final
          if (onProgress) {
            onProgress({ loaded: file.size, total: file.size, percentage: 100 })
          }

          return finishResponse.result
        } else {
          // Chunks intermedios
          await this.dbx.filesUploadSessionAppendV2({
            cursor: {
              session_id: sessionId,
              offset: offset,
            },
            contents: chunkBuffer,
            close: false,
          })

          offset += chunk.size

          // Reportar progreso
          if (onProgress) {
            const percentage = Math.round((offset / file.size) * 100)
            onProgress({ loaded: offset, total: file.size, percentage })
          }
        }
      }

      throw new Error('Error inesperado en la subida')
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      throw new Error(`Error al subir archivo grande: ${errorMessage}`)
    }
  }

  /**
   * Método inteligente que elige automáticamente la estrategia correcta
   */
  async upload(
    file: File,
    path: string,
    onProgress?: (progress: UploadProgress) => void,
  ): Promise<files.FileMetadata> {
    const MAX_SIMPLE_UPLOAD = 150 * 1024 * 1024 // 150MB
    const MAX_FILE_SIZE = 350 * 1024 * 1024 * 1024 // 350GB

    // Validar tamaño máximo
    if (file.size > MAX_FILE_SIZE) {
      throw new Error(`El archivo excede el tamaño máximo permitido de 350GB`)
    }

    // Elegir estrategia según tamaño
    if (file.size <= MAX_SIMPLE_UPLOAD) {
      return this.uploadSmallFile(file, path, onProgress)
    } else {
      return this.uploadLargeFile(file, path, onProgress)
    }
  }

  /**
   * Subir múltiples archivos en paralelo (batch upload)
   * Útil para subir varios archivos pequeños al mismo tiempo
   */
  async uploadBatch(
    files: File[],
    basePath: string,
    onProgress?: (fileIndex: number, progress: UploadProgress) => void,
  ): Promise<files.FileMetadata[]> {
    const uploadPromises = files.map((file, index) => {
      const filePath = `${basePath}/${file.name}`
      return this.upload(file, filePath, (progress) => {
        if (onProgress) {
          onProgress(index, progress)
        }
      })
    })

    return Promise.all(uploadPromises)
  }

  async getUrl(path: string): Promise<string> {
    try {
      const resp = await this.dbx.sharingListSharedLinks({
        path: path,
        direct_only: true,
      })

      if (resp.result.links.length > 0) {
        return resp.result.links[0].url
      }

      const newLink = await this.dbx.sharingCreateSharedLinkWithSettings({
        path: path,
        settings: {
          requested_visibility: { '.tag': 'public' },
          audience: { '.tag': 'public' },
          access: { '.tag': 'viewer' },
        },
      })

      return newLink.result.url
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'status' in error) {
        const dbxError = error as { status: number; error: unknown }

        if (dbxError.status === 409) {
          throw new Error(`La carpeta del usuario "${path}" no existe en Dropbox`)
        }
      }
      const errorMessage = error instanceof Error ? error.message : String(error)
      throw new Error(`Error al buscar query: ${path}: ${errorMessage}`)
    }
  }
}

export const dropboxService = new DropboxService()
